import { Camera } from "lucide-react";
/**
 * Piezas comunes a la tarjeta de resultado y al detalle del aviso: patentes con su
 * procedencia, extractos con coincidencias resaltadas y el sello de la fuente.
 */

import type { IdentifierView, SnippetSegment } from "../../shared/contracts.js";
import { cx } from "../lib/cx.js";
import { fmtPlate } from "../lib/format.js";
import { identifierProvenance, sourceLabel } from "../lib/labels.js";
import { Badge } from "./ui/Badge.js";
import { Tooltip } from "./ui/Tooltip.js";

/**
 * Patente: las fuertes (dato del aviso, URL, OCR con alta confianza) van sólidas; las
 * débiles van punteadas para que el analista no las tome por un hecho.
 */
export function PlateBadge({ plate, size = "sm" }: { plate: IdentifierView; size?: "sm" | "md" }) {
  const strong = plate.strength === "strong";
  // Vista en una foto del aviso, en otro vehículo o un letrero: no es la del auto publicado.
  const inPhoto = plate.source === "ocr_foto";
  const provenance = identifierProvenance(plate);
  return (
    <Tooltip content={provenance}>
      <span
        className={cx(
          "inline-flex items-center rounded-md border font-mono font-semibold tracking-wider tabular-nums",
          size === "sm" ? "h-6 px-1.5 text-[12px]" : "h-8 px-2.5 text-[14px]",
          strong
            ? "border-fg/70 bg-surface text-fg"
            : "border-dashed border-border-strong bg-surface-2 text-muted",
        )}
      >
        <span className="sr-only">{inPhoto ? "Patente vista en una foto " : "Patente "}</span>
        {fmtPlate(plate.value)}
        {inPhoto ? (
          <Camera aria-hidden="true" className="ml-1 size-3 shrink-0" />
        ) : !strong && (
          <span aria-hidden="true" className="ml-1 text-[10px] font-medium tracking-normal">
            ?
          </span>
        )}
      </span>
    </Tooltip>
  );
}

export function PlateList({ plates, size, max }: { plates: IdentifierView[]; size?: "sm" | "md"; max?: number }) {
  // Fuertes primero: son las que el analista debe ver aunque se recorte la lista.
  // Las vistas en fotos (otro vehículo) al final.
  const sorted = [...plates].sort((a, b) =>
    Number(b.strength === "strong") - Number(a.strength === "strong") || Number(a.source === "ocr_foto") - Number(b.source === "ocr_foto"));
  const shown = max ? sorted.slice(0, max) : sorted;
  const rest = sorted.length - shown.length;
  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      {shown.map((p) => (
        <PlateBadge key={`${p.value}-${p.source}`} plate={p} size={size} />
      ))}
      {rest > 0 && <span className="text-[12px] text-subtle">+{rest}</span>}
    </span>
  );
}

/** Extracto en segmentos (nunca HTML): los tramos coincidentes van en <mark>. */
export function SnippetText({ segments, className }: { segments: SnippetSegment[]; className?: string }) {
  if (segments.length === 0) return null;
  return (
    <p className={cx("text-[13px] leading-relaxed text-muted", className)}>
      {segments.map((s, i) => (s.hit ? <mark key={i}>{s.text}</mark> : <span key={i}>{s.text}</span>))}
    </p>
  );
}

export function SourceBadge({ source }: { source: string }) {
  return (
    <Badge tone="outline" className="bg-surface/95" title={`Fuente: ${sourceLabel(source)}`}>
      {sourceLabel(source)}
    </Badge>
  );
}
